import { useRef, useState, useEffect } from 'react'
import { ArrowUp, Loader2} from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useChatContext } from '@/features/workstation/hooks/useChatContext'
import { useSearchGroups } from '@/features/workstation/hooks/useSearchGroups'
import { useAutoScroll } from '@/features/workstation/hooks/useAutoScroll'
import { Message } from './message'
import { WorkspaceTabs } from './workspace-tabs'
import { SuggestedQuestions } from './suggested-questions'
import { HorizontalSuggestedQuestions } from './horizontal-suggested-questions'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { VideoModal } from './video-modal'
import { useGlobalSourceManagementStore } from '@/stores/globalSourceManagementStore'
import FolderSelector from './folder-selector'
import { ToolsDropdown } from './tools/tools-dropdown'
import { shouldShowChatLoading } from '@/utils/chat-loading-detector'
import { AnimatePresence } from 'framer-motion'
import { WorkstationLoading } from './workstation-loading'

export interface ClickedVideo {
  videoId: string | null;
  startTime: number;
}

export default function WorkstationChat() {
  const {t} = useTranslation();
  const globalSourceManager = useGlobalSourceManagementStore();
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('sources')
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [navigatedToSourcesBySystem, setNavigatedToSourcesBySystem] = useState(false)
  const [clickedVideo, setClickedVideo] = useState<ClickedVideo>({
    videoId: null,
    startTime: 0, 
  });

  useEffect(() => {
    if(globalSourceManager.activeSourceId) {
      setActiveTab("sources");
    }
  }, [globalSourceManager]);
  
  function handleCloseVideoModal() {
    setClickedVideo({
      startTime: 0,
      videoId: null,
    })
  };

  const { messages, handleSubmit, input, setInput, isLoading, deepSearch, toggleDeepSearch, externalSearch, toggleExternalSearch } =
    useChatContext()

  const { messagesContainerRef, messagesEndRef, scrollToBottom } = 
    useAutoScroll<HTMLDivElement>(messages, isSubmitting)

  const searchGroups = useSearchGroups(messages, isLoading)

   const displayableSearchGroups = {
    ...searchGroups,
    webSearchResults: searchGroups.webSearchResults.map(group => ({
      ...group,
      results: [...group.results].sort((a, b) => b.score - a.score)
    })).sort((a, b) => a.timestamp - b.timestamp),
    fileSearchResults: searchGroups.fileSearchResults.map(group => ({
      ...group,
      results: [...group.results].sort((a, b) => b.score - a.score)
    })).sort((a, b) => a.timestamp - b.timestamp),
  };

  const adjustTextareaHeight = () => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 100)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  useEffect(() => {
    if (isLoading) {
      setActiveTab('reasoning');
      setNavigatedToSourcesBySystem(true)
    } else if (!isLoading && messages.length > 0) {
      setActiveTab('sources');
      setNavigatedToSourcesBySystem(false)
    }
  }, [isLoading, messages.length]);

  const handleUserMessageSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setActiveTab('reasoning')
    handleSubmit(e)
    scrollToBottom()
    setTimeout(() => {
      setIsSubmitting(false)
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
      }
    }, 200)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const form = e.currentTarget.form
      if (form) {
        const event = new Event('submit', { bubbles: true, cancelable: true })
        form.dispatchEvent(event)
      }
    }
  }

  const handleSuggestedQuestion = (question: string) => {
    setInput(question)
    requestAnimationFrame(() => {
      if(inputRef.current) {
        inputRef.current.focus();
        adjustTextareaHeight();
        
        const form = inputRef.current.form;
        if(form) {
          const event = new Event("submit", { bubbles: true, cancelable: true });
          form.dispatchEvent(event);
        }
      }
    });
  }

  const isMessageLoading = () => {
    return shouldShowChatLoading(messages, isLoading)
  }

  return (
    <div className='flex h-[calc(100vh-12rem)] gap-4'>
      <Card className='flex flex-1 flex-col overflow-hidden'>
        <div ref={messagesContainerRef} className='flex-1 overflow-y-auto p-4 pb-6'>
          {messages.length === 0 ? (
            <div className='flex h-full flex-col items-center justify-center'>
              <div className='w-full max-w-lg'>
                <div className='flex flex-col items-center space-y-6 text-center'>
                  <div className='flex h-20 w-20 items-center justify-center rounded-full'>
                    <img
                      src='brand/alan/favicon/alan-touch-icon-180x180.png'
                      alt=''
                      className='h-22 w-22 text-primary'
                    />
                  </div>
                  <div className='space-y-2'>
                    <h1 className='text-2xl font-bold tracking-tight mt-10 md:mt-0'>
                      {t(tokens.workstation.welcome)}
                    </h1>
                    <p className='text-muted-foreground'>
                      {t(tokens.workstation.welcomeSubtitle)}
                      {/* Start a conversation and explore topics with Alan, our AI
                      assistant. */}
                    </p>
                  </div>
                  <SuggestedQuestions onQuestionClick={handleSuggestedQuestion} />
                </div>
              </div>
            </div>
          ) : (
            <AnimatePresence>
              <div className='flex flex-col'>
                {messages.map((message) => {
                  return (
                    <Message
                      key={message.id}
                      message={message}
                      isLoading={isMessageLoading()}
                      ref={(el) => {
                        messageRefs.current[message.id] = el
                      }}
                    />
                  )
                })}
                <div ref={messagesEndRef} />
              </div>
              {isMessageLoading() && <WorkstationLoading />}
            </AnimatePresence>
          )}
        </div>
        <div className='border-t p-4'>
          <form onSubmit={handleUserMessageSubmit}>
            {messages.length > 0 && (
              <HorizontalSuggestedQuestions onQuestionClick={handleSuggestedQuestion} />
            )}
            <div className='border border-input rounded-lg px-1'>
              <FolderSelector />
              <textarea
                ref={inputRef}
                className='w-full bg-background resize-none pt-3 px-4 pb-3 text-sm focus-visible:outline-none focus-visible:ring-0 disabled:opacity-70 disabled:cursor-not-allowed'
                rows={1}
                placeholder={isLoading ? 'Please wait for Alan to respond...' : t(tokens.workstation.prompt.placeholder)}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                }}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                style={{ minHeight: '40px', maxHeight: '200px', overflow: 'auto' }}
              />
              <div className='h-9 bg-background flex items-center px-3 justify-between'>
                <ToolsDropdown 
                  externalSearch={externalSearch} 
                  deepSearch={deepSearch} 
                  toggleExternalSearch={toggleExternalSearch} 
                  toggleDeepSearch={toggleDeepSearch}
                  isLoading={isLoading}
                />
                {isLoading ? (
                  <Button
                    type='button'
                    variant={'outline'}
                    size='icon'
                    className='h-7 w-7 rounded-full'
                    disabled={true}
                  >
                    <Loader2 className='h-4 w-4 animate-spin' />
                  </Button>
                ) : (
                  <Button
                    type='submit'
                    variant={'outline'}
                    size='icon'
                    className='h-7 w-7 rounded-full'
                    disabled={isLoading || !input.trim().length}
                  >
                    <ArrowUp className='h-6 w-6 text-primary' />
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </Card>

      <div className='hidden md:block'>
        <WorkspaceTabs 
          searchGroups={displayableSearchGroups}
          messages={messages} 
          activeTab={activeTab}
          onTabChange={(tab) => {
            setNavigatedToSourcesBySystem(false);
            setActiveTab(tab);
          }}
          isWebsearchOn={externalSearch}
          messageRefs={messageRefs}
          navigatedToSourcesBySystem={navigatedToSourcesBySystem}
        />
      </div>
     {clickedVideo.videoId && 
      <VideoModal 
        clickedVideo={clickedVideo} 
        onClose={handleCloseVideoModal}
      />
      } 
    </div>
  )
}
